import { type FC, type ReactNode, useState } from "react"
import styled, { createGlobalStyle } from "styled-components"

import Metadata, { type MetadataProps } from "~/components/Metadata"
import { usePlatform } from "~/contexts/PlatformContext"
import CookieConsent from "~/themes/school/components/CookieConsent"
import { BackIcon, CloseIcon, MenuIcon } from "~/themes/school/components/Icon"
import {
	backgroundColor,
	dangerColor,
	lightDangerColor,
	lightSuccessColor,
	lightTextColor,
	successColor,
} from "~/themes/school/styles"

import Footer from "./Footer"
import MobileHeader from "./MobileHeader"
import NavigationMenu from "./NavigationMenu"
import Sidebar from "./Sidebar"

export const Styles = createGlobalStyle`
	:root {
		--color-light: ${lightTextColor};
		--color-success: ${successColor};
		--color-success-light: ${lightSuccessColor};
		--color-danger: ${dangerColor};
		--color-danger-light: ${lightDangerColor};
	}

	body {
		font-family: "Work Sans", sans-serif;
	}
`

type Props = {
	metadata?: MetadataProps
	onBackButtonClick?: string | (() => void)
	className?: string
	children?: ReactNode
}
const Layout: FC<Props> = ({ metadata, onBackButtonClick, className, children }) => {
	const { platform } = usePlatform()

	const [isMenuOpen, setMenuOpen] = useState(false)

	return (
		<Wrapper>
			<Metadata
				{...(metadata ?? {
					title: platform.title ?? platform.name,
					description: platform.description ?? undefined,
					imageUrl: platform.thumbnailFilePath ?? undefined,
				})}
			/>
			<CookieConsent />
			<Container>
				<Sidebar />
				<MobileHeader
					leftAction={
						onBackButtonClick === undefined
							? {
									icon: isMenuOpen ? CloseIcon : MenuIcon,
									onClick: () => setMenuOpen(current => !current),
								}
							: { icon: BackIcon, onClick: onBackButtonClick }
					}
				/>
				<Content className={className}>{children}</Content>
				<NavigationMenu isOpen={isMenuOpen} />
				<Footer />
			</Container>
		</Wrapper>
	)
}

const Wrapper = styled.div`
	width: 100%;
	background-color: ${backgroundColor};
	display: flex;
	justify-content: center;
`

const Container = styled.div`
	width: 100%;
	min-height: 100vh;
	position: relative;
	display: flex;
	flex-direction: column;

	@media (min-width: 1000px) {
		margin-left: 250px;
	}
`

const Content = styled.div`
	padding: 24px;
	display: flex;
	flex-direction: column;
	flex: 1;

	@media (min-width: 1000px) {
		padding: 42px;
	}
`

export default Layout
